import { useLocation, useNavigate } from "react-router";

import { showToast } from "@/components/ui/toast";
import { GUEST_ONLY_ROUTES, PROTECTED_ROUTES } from "@/constants/routes";
import { useToggleWishlist } from "@/features/engagement/hooks/useToggleWishlist";
import { useAppSelector } from "@/store/hooks";
import { ApiError } from "@/types/api";

/**
 * `ProductCard`'s `onFavoriteToggle` is `(productId: string) => void` — one
 * id, no `isFavorite` flag, since the card itself doesn't track membership.
 * This hook closes over the caller's own favorited-ids `Set` (derived from
 * `useWishlist()`) so every page wires the same guest-redirect +
 * add-or-remove logic instead of re-deriving it — the same
 * check-auth-then-redirect-with-`?redirect=`-then-return pattern
 * `ProductDetailsPage`'s "Message Seller" button already established.
 *
 * The toggle used to be silent — no confirmation it worked, no feedback if
 * it failed. Now shows a toast either way: success gets a "View Wishlist"
 * action (optional, per Toast's own "doesn't require the user to act"
 * purpose — the wishlist is still reachable from the header regardless),
 * failure surfaces the actual error instead of the heart icon just not
 * changing with no explanation.
 */
export function useFavoriteToggleHandler(favoriteIds: Set<string>) {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useAppSelector((state) => state.auth.status === "authenticated");
  const toggleMutation = useToggleWishlist();

  return function handleFavoriteToggle(productId: string) {
    if (!isAuthenticated) {
      const redirect = encodeURIComponent(`${location.pathname}${location.search}`);
      navigate(`${GUEST_ONLY_ROUTES.login}?redirect=${redirect}`);
      return;
    }

    const wasAlreadyFavorite = favoriteIds.has(productId);
    toggleMutation.mutate(
      { productId, isFavorite: wasAlreadyFavorite },
      {
        onSuccess: () => {
          if (wasAlreadyFavorite) {
            showToast.success("Removed from wishlist.");
          } else {
            showToast.success("Added to wishlist.", {
              label: "View Wishlist",
              onClick: () => navigate(PROTECTED_ROUTES.wishlist),
            });
          }
        },
        onError: (error) => {
          showToast.error(
            error instanceof ApiError ? error.message : "Couldn't update your wishlist.",
          );
        },
      },
    );
  };
}
